import { useState, useEffect } from 'react';
import { scrapApprovalsApi, ScrapApproval } from '../../api/scrap-approvals.api';
import { inventoryApi } from '../../api/inventory.api';
import Table, { Column } from '../../components/common/Table';
import Button from '../../components/forms/Button';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import Input from '../../components/forms/Input';
import ImageViewerModal from '../../components/common/ImageViewerModal';
import { useAuth } from '../../hooks/useAuth';

const ScrapApprovalList = () => {
  const { user } = useAuth();
  const [approvals, setApprovals] = useState<ScrapApproval[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('pending');
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedApproval, setSelectedApproval] = useState<ScrapApproval | null>(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const isAdmin = user?.role === 'Admin';

  useEffect(() => {
    fetchApprovals();
  }, [filterStatus]);

  const fetchApprovals = async () => {
    try {
      setIsLoading(true);
      const data = await scrapApprovalsApi.getAll(filterStatus !== 'all' ? filterStatus : undefined);
      setApprovals(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Hurda onayları yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenApproveModal = (approval: ScrapApproval) => {
    setSelectedApproval(approval);
    setApprovalNotes('');
    setIsApproveModalOpen(true);
  };

  const handleOpenRejectModal = (approval: ScrapApproval) => {
    setSelectedApproval(approval);
    setRejectionReason('');
    setIsRejectModalOpen(true);
  };

  const handleOpenImageViewer = (images: string[], index: number = 0) => {
    setSelectedImages(images);
    setSelectedImageIndex(index);
    setIsImageViewerOpen(true);
  };

  const handleApprove = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedApproval) return;

    try {
      // Approve the scrap request
      await scrapApprovalsApi.approve(selectedApproval.id, { notes: approvalNotes });

      // Extract device ID
      let deviceId: string;
      if (typeof selectedApproval.device === 'string') {
        deviceId = selectedApproval.device;
      } else if (selectedApproval.device && typeof selectedApproval.device === 'object') {
        deviceId = selectedApproval.device._id || selectedApproval.device.id;
      } else {
        throw new Error('Cihaz bilgisi bulunamadı');
      }

      // Extract location info
      let fromLocation = '';
      let fromLocationId: string | undefined;
      
      if (typeof selectedApproval.device === 'object' && selectedApproval.device?.currentLocation) {
        const location = selectedApproval.device.currentLocation;
        fromLocation = typeof location === 'object' ? location.name : location;
        fromLocationId = typeof location === 'object' ? (location._id || location.id) : undefined;
      }

      // Create the scrap movement in inventory
      await inventoryApi.createMovement({
        product: deviceId,
        serialNumber: selectedApproval.serialNumber,
        type: 'Scrap',
        date: new Date().toISOString().split('T')[0],
        fromLocation: fromLocation || 'Bilinmiyor',
        fromLocationId: fromLocationId,
        toLocation: 'Hurda',
        notes: `Onaylayan: ${user?.email}${approvalNotes ? `\n${approvalNotes}` : ''}`,
      });

      await fetchApprovals();
      setIsApproveModalOpen(false);
      setSelectedApproval(null);
    } catch (err: any) {
      setError(err.message || 'Onaylama işlemi başarısız oldu');
    }
  };

  const handleReject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedApproval || !rejectionReason) {
      setError('Ret nedeni zorunludur');
      return;
    }

    try {
      await scrapApprovalsApi.reject(selectedApproval.id, rejectionReason);
      await fetchApprovals();
      setIsRejectModalOpen(false);
      setSelectedApproval(null);
    } catch (err: any) {
      setError(err.message || 'Reddetme işlemi başarısız oldu');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu kaydı silmek istediğinizden emin misiniz?')) {
      return;
    }
    try {
      await scrapApprovalsApi.delete(id);
      await fetchApprovals();
    } catch (err: any) {
      setError(err.message || 'Silme işlemi başarısız oldu');
    }
  };

  const getDeviceName = (device: any): string => {
    if (typeof device === 'object' && device !== null) {
      return `${device.name} (${device.serialNumber})`;
    }
    return '-';
  };

  const getUserName = (user: any): string => {
    if (typeof user === 'object' && user !== null) {
      if (user.firstName && user.lastName) {
        return `${user.firstName} ${user.lastName}`;
      }
      return user.email || user.username || '-';
    }
    return '-';
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    const labels: Record<string, string> = {
      pending: 'Bekliyor',
      approved: 'Onaylandı',
      rejected: 'Reddedildi',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const columns: Column<ScrapApproval>[] = [
    {
      key: 'createdAt',
      header: 'Talep Tarihi',
      render: (item) => new Date(item.createdAt).toLocaleDateString('tr-TR'),
    },
    { key: 'serialNumber', header: 'Seri No' },
    { key: 'device', header: 'Cihaz', render: (item) => getDeviceName(item.device) },
    { key: 'reason', header: 'Sebep', render: (item) => item.reason || '-' },
    {
      key: 'photos',
      header: 'Fotoğraflar',
      render: (item) => (
        item.photos && item.photos.length > 0 ? (
          <div className="flex gap-1">
            {item.photos.slice(0, 3).map((photo, index) => (
              <img
                key={index}
                src={photo}
                alt={`Fotoğraf ${index + 1}`}
                className="w-12 h-12 object-cover rounded border border-gray-300 cursor-pointer hover:scale-110 transition-transform"
                onClick={() => handleOpenImageViewer(item.photos!, index)}
                title="Görüntülemek için tıklayın"
              />
            ))}
            {item.photos.length > 3 && (
              <div 
                className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded border border-gray-300 text-xs text-gray-600 cursor-pointer hover:bg-gray-200 transition-colors"
                onClick={() => handleOpenImageViewer(item.photos!, 3)}
                title="Tüm fotoğrafları görüntüle"
              >
                +{item.photos.length - 3}
              </div>
            )}
          </div>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )
      ),
    },
    { key: 'requestedBy', header: 'Talep Eden', render: (item) => getUserName(item.requestedBy) },
    { key: 'status', header: 'Durum', render: (item) => getStatusBadge(item.status) },
    {
      key: 'approvedBy',
      header: 'Onaylayan',
      render: (item) => (item.approvedBy ? getUserName(item.approvedBy) : '-'),
    },
    {
      key: 'actions',
      header: 'İşlemler',
      render: (item) => (
        <div className="flex gap-2">
          {item.status === 'pending' && isAdmin && (
            <>
              <Button size="sm" variant="primary" onClick={() => handleOpenApproveModal(item)}>
                Onayla
              </Button>
              <Button size="sm" variant="danger" onClick={() => handleOpenRejectModal(item)}>
                Reddet
              </Button>
            </>
          )}
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => handleDelete(item.id)}>
              Sil
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text="Hurda onayları yükleniyor..." />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Hurda Onayları</h1>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {!isAdmin && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 text-blue-700 rounded-md">
          ℹ️ Hurda onayları sadece Admin kullanıcılar tarafından onaylanabilir
        </div>
      )}

      {/* Filter */}
      <div className="mb-6 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Durum:</label>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={filterStatus === 'pending' ? 'primary' : 'outline'}
              onClick={() => setFilterStatus('pending')}
            >
              Bekleyenler
            </Button>
            <Button
              size="sm"
              variant={filterStatus === 'approved' ? 'primary' : 'outline'}
              onClick={() => setFilterStatus('approved')}
            >
              Onaylananlar
            </Button>
            <Button
              size="sm"
              variant={filterStatus === 'rejected' ? 'primary' : 'outline'}
              onClick={() => setFilterStatus('rejected')}
            >
              Reddedilenler
            </Button>
            <Button
              size="sm"
              variant={filterStatus === 'all' ? 'primary' : 'outline'}
              onClick={() => setFilterStatus('all')}
            >
              Tümü
            </Button>
          </div>
        </div>
      </div>

      <Table data={approvals} columns={columns} emptyMessage="Henüz hurda onayı bulunmamaktadır" />

      {/* Approve Modal */}
      <Modal
        isOpen={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
        title="Hurda Onayı"
        size="md"
      >
        <form onSubmit={handleApprove}>
          {selectedApproval && (
            <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
              <p className="text-sm font-semibold text-gray-900 mb-2">Cihaz Bilgileri</p>
              <p className="text-sm text-gray-700">
                <strong>Cihaz:</strong> {getDeviceName(selectedApproval.device)}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Seri No:</strong> {selectedApproval.serialNumber}
              </p>
              {selectedApproval.reason && (
                <p className="text-sm text-gray-700">
                  <strong>Sebep:</strong> {selectedApproval.reason}
                </p>
              )}
              {selectedApproval.notes && (
                <p className="text-sm text-gray-700">
                  <strong>Notlar:</strong> {selectedApproval.notes}
                </p>
              )}
              {selectedApproval.photos && selectedApproval.photos.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Fotoğraflar:</p>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedApproval.photos.map((photo, index) => (
                      <img
                        key={index}
                        src={photo}
                        alt={`Fotoğraf ${index + 1}`}
                        className="w-full h-24 object-cover rounded border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => handleOpenImageViewer(selectedApproval.photos!, index)}
                        title="Görüntülemek için tıklayın"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <Input
            label="Onay Notu (Opsiyonel)"
            value={approvalNotes}
            onChange={(e) => setApprovalNotes(e.target.value)}
            placeholder="Onay ile ilgili notlar..."
          />

          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              ⚠️ Bu işlem onaylandığında cihaz hurdaya gönderilecek ve stoktan düşecektir.
            </p>
          </div>

          <div className="flex gap-2 mt-6">
            <Button type="submit" fullWidth>
              Onayla ve Hurdaya Gönder
            </Button>
            <Button type="button" variant="outline" fullWidth onClick={() => setIsApproveModalOpen(false)}>
              İptal
            </Button>
          </div>
        </form>
      </Modal>

      {/* Reject Modal */}
      <Modal
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        title="Hurda Talebi Reddet"
        size="md"
      >
        <form onSubmit={handleReject}>
          {selectedApproval && (
            <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
              <p className="text-sm font-semibold text-gray-900 mb-2">Cihaz Bilgileri</p>
              <p className="text-sm text-gray-700">
                <strong>Cihaz:</strong> {getDeviceName(selectedApproval.device)}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Seri No:</strong> {selectedApproval.serialNumber}
              </p>
            </div>
          )}

          <Input
            label="Ret Nedeni"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Neden reddedildiğini açıklayın..."
            required
          />

          <div className="flex gap-2 mt-6">
            <Button type="submit" variant="danger" fullWidth>
              Reddet
            </Button>
            <Button type="button" variant="outline" fullWidth onClick={() => setIsRejectModalOpen(false)}>
              İptal
            </Button>
          </div>
        </form>
      </Modal>

      {/* Image Viewer Modal */}
      <ImageViewerModal
        isOpen={isImageViewerOpen}
        onClose={() => setIsImageViewerOpen(false)}
        images={selectedImages}
        initialIndex={selectedImageIndex}
      />
    </div>
  );
};

export default ScrapApprovalList;
